// @spec POL-STR-040
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { Plus, Shield } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  AccessRuleFormDialog,
  AccessRulesList,
  DeleteAccessRuleDialog,
  useAccessRules,
  useCategories,
  useCreateAccessRule,
  useDeleteAccessRule,
  usePolicies,
  useUpdateAccessRule,
} from '@/features/policies';
import type {
  CreateAccessRuleInput,
  PolicyAccessRule,
  UpdateAccessRuleInput,
} from '@/features/policies';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

// Placeholder hooks for roles and users - these would typically come from IAM feature
function useRoles() {
  // In a real implementation, this would fetch from /api/v1/roles
  return {
    data: [] as { id: string; name: string; displayName?: string }[],
    isLoading: false,
  };
}

function useOrgUsers() {
  // In a real implementation, this would fetch from /api/v1/members
  return {
    data: [] as {
      id: string;
      email: string;
      firstName?: string | null;
      lastName?: string | null;
    }[],
    isLoading: false,
  };
}

export const Route = createFileRoute('/_authenticated/admin/policies/access-controls')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.policyAccessControls'),
  }),
  component: PolicyAccessControlsPage,
});

/**
 * Admin page for managing policy access rules.
 *
 * Allows organization admins to:
 * - View all access rules
 * - Create new rules for policies or categories
 * - Edit permission levels
 * - Delete access rules
 */
function PolicyAccessControlsPage() {
  const { t } = useTranslation('admin');
  const { isAdmin } = useOrgRole();

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PolicyAccessRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<PolicyAccessRule | null>(null);

  // API hooks
  const { data: rulesData, isLoading: isLoadingRules } = useAccessRules({});
  const { data: policiesData } = usePolicies({
    filters: {
      status: null,
      categoryId: null,
      frameworkId: null,
      type: null,
      search: null,
      sortBy: 'title',
      sortOrder: 'asc',
    },
  });
  const { data: categories } = useCategories();
  const { data: roles } = useRoles();
  const { data: users } = useOrgUsers();

  const createMutation = useCreateAccessRule();
  const updateMutation = useUpdateAccessRule();
  const deleteMutation = useDeleteAccessRule();

  const rules = rulesData?.items ?? [];
  const policies = policiesData?.pages.flatMap((page) => page.items) ?? [];

  // Handlers
  const handleCreateOpen = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (rule: PolicyAccessRule) => {
    setEditingRule(rule);
  };

  const handleDelete = (rule: PolicyAccessRule) => {
    setDeletingRule(rule);
  };

  const handleCreateSubmit = async (
    data: CreateAccessRuleInput | UpdateAccessRuleInput,
    isUpdate: boolean
  ) => {
    if (isUpdate) {
      // This shouldn't happen from create dialog
      return;
    }

    try {
      await createMutation.mutateAsync(data as CreateAccessRuleInput);
      toast.success(t('admin:toast.accessRuleCreatedSuccessfully'));
      setIsCreateDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create access rule';
      toast.error(message);
      throw error;
    }
  };

  const handleUpdateSubmit = async (
    data: CreateAccessRuleInput | UpdateAccessRuleInput,
    isUpdate: boolean
  ) => {
    if (!editingRule || !isUpdate) return;

    try {
      await updateMutation.mutateAsync({
        ruleId: editingRule.id,
        input: data as UpdateAccessRuleInput,
      });
      toast.success(t('admin:toast.accessRuleUpdatedSuccessfully'));
      setEditingRule(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update access rule';
      toast.error(message);
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRule) return;

    try {
      await deleteMutation.mutateAsync(deletingRule.id);
      toast.success(t('admin:toast.accessRuleDeletedSuccessfully'));
      setDeletingRule(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete access rule';
      toast.error(message);
      throw error;
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <h2 className="text-lg font-medium text-destructive">{t('common:accessDenied.title')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('common:accessDenied.policyAccessControlsDenied')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <header className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900">
              <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Policy Access Controls</h1>
              <p className="mt-1 text-muted-foreground">
                Manage who can view, edit, and approve policies in your organization.
              </p>
            </div>
          </div>

          <Button onClick={handleCreateOpen}>
            <Plus className="mr-2 h-4 w-4" />
            Add Rule
          </Button>
        </header>

        {/* Stats summary */}
        {!isLoadingRules && rules.length > 0 && (
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span>
              {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
            </span>
            <span>{rules.filter((r) => r.policyId).length} policy-specific</span>
            <span>{rules.filter((r) => r.categoryId).length} category-wide</span>
          </div>
        )}

        {/* Rules list */}
        <AccessRulesList
          rules={rules}
          isLoading={isLoadingRules}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Create Dialog */}
        <AccessRuleFormDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          policies={policies}
          categories={categories ?? []}
          roles={roles}
          users={users}
          onSubmit={handleCreateSubmit}
          isSubmitting={createMutation.isPending}
        />

        {/* Edit Dialog */}
        {editingRule && (
          <AccessRuleFormDialog
            open={!!editingRule}
            onOpenChange={(open) => {
              if (!open) setEditingRule(null);
            }}
            rule={editingRule}
            policies={policies}
            categories={categories ?? []}
            roles={roles}
            users={users}
            onSubmit={handleUpdateSubmit}
            isSubmitting={updateMutation.isPending}
          />
        )}

        {/* Delete Dialog */}
        <DeleteAccessRuleDialog
          open={!!deletingRule}
          onOpenChange={(open) => {
            if (!open) setDeletingRule(null);
          }}
          rule={deletingRule}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteMutation.isPending}
        />
      </div>
    </div>
  );
}
